import { Controller } from "@hotwired/stimulus"

const GROUPS = ["stateInclude", "stateExclude", "msaInclude", "msaExclude"]

// Handles filtering + selection on alert result tables.
// - Filters: checkbox dropdowns for State include/exclude and MSA
//   include/exclude, plus a numeric Min Population. All applied client-side,
//   debounced.
// - Selection: up to 5 geographies for the "View in Dashboard" workflow.
//
// Per-click work stays O(1) via a Set of checked ids. The only time we
// touch every checkbox in the result table is when the selection count
// crosses the cap of 5.
export default class extends Controller {
  static targets = [
    "checkbox", "viewBtn", "count", "clearBtn",
    "row", "visibleCount",
    "popMinFilter", "clearFiltersBtn",
    "stateIncludeCheckbox", "stateExcludeCheckbox",
    "msaIncludeCheckbox", "msaExcludeCheckbox",
    "stateIncludeLabel", "stateExcludeLabel",
    "msaIncludeLabel", "msaExcludeLabel"
  ]

  connect() {
    this.maxSelections = 5
    this._lastAtMax = false
    this.checkedIds = new Set()
    this.updateState()
    // Set initial filter labels (and bail out early — no filters set yet,
    // nothing to iterate).
    GROUPS.forEach(g => this._updateGroupLabel(g))
  }

  disconnect() {
    if (this._filterTimer) clearTimeout(this._filterTimer)
  }

  // ---------- Filter triggers ----------

  applyFilters() {
    clearTimeout(this._filterTimer)
    this._filterTimer = setTimeout(() => this._runFilters(), 180)
  }

  clearGroup(event) {
    event.preventDefault()
    const group = event.currentTarget.dataset.group
    if (!GROUPS.includes(group)) return
    this._checkboxesFor(group).forEach(cb => { cb.checked = false })
    this._updateGroupLabel(group)
    this.applyFilters()
  }

  searchFilter(event) {
    const group = event.currentTarget.dataset.group
    if (!GROUPS.includes(group)) return
    const q = event.currentTarget.value.trim().toLowerCase()
    this._checkboxesFor(group).forEach(cb => {
      const label = cb.closest("label")
      if (!label) return
      const text = (label.textContent || "").trim().toLowerCase()
      label.style.display = !q || text.includes(q) ? "" : "none"
    })
  }

  clearFilters() {
    GROUPS.forEach(g => {
      this._checkboxesFor(g).forEach(cb => { cb.checked = false })
      this._updateGroupLabel(g)
    })
    if (this.hasPopMinFilterTarget) this.popMinFilterTarget.value = ""
    clearTimeout(this._filterTimer)
    this._runFilters()
  }

  // ---------- Filter execution ----------

  _runFilters() {
    const stateIncludes = this._checkedValues("stateInclude").map(v => v.toUpperCase())
    const stateExcludes = this._checkedValues("stateExclude").map(v => v.toUpperCase())
    const msaIncludes = new Set(this._checkedValues("msaInclude"))
    const msaExcludes = new Set(this._checkedValues("msaExclude"))

    const popMinRaw = this.hasPopMinFilterTarget ? this.popMinFilterTarget.value : ""
    const popMin = popMinRaw ? parseInt(popMinRaw, 10) : 0

    // Update each dropdown's summary label.
    GROUPS.forEach(g => this._updateGroupLabel(g))

    let visible = 0
    this.rowTargets.forEach(row => {
      const rowState = (row.dataset.state || "").toUpperCase()
      const rowMsa = row.dataset.msa || ""

      const matchStateInclude = !stateIncludes.length || stateIncludes.includes(rowState)
      const matchStateExclude = !stateExcludes.length || !stateExcludes.includes(rowState)
      const matchMsaInclude = !msaIncludes.size || msaIncludes.has(rowMsa)
      const matchMsaExclude = !msaExcludes.size || !msaExcludes.has(rowMsa)

      const pop = parseInt(row.dataset.population, 10) || 0
      const matchPop = !popMin || pop >= popMin

      const show = matchStateInclude && matchStateExclude && matchMsaInclude && matchMsaExclude && matchPop
      if (row.hidden !== !show) row.hidden = !show
      if (show) visible++
    })

    if (this.hasVisibleCountTarget) this.visibleCountTarget.textContent = visible

    const anyFilter = Boolean(
      stateIncludes.length || stateExcludes.length ||
      msaIncludes.size || msaExcludes.size ||
      popMin > 0
    )
    if (this.hasClearFiltersBtnTarget) {
      this.clearFiltersBtnTarget.style.display = anyFilter ? "" : "none"
    }
  }

  // ---------- Selection ----------

  toggle(event) {
    const cb = event?.target
    if (!cb) return
    if (cb.checked) this.checkedIds.add(cb.value)
    else this.checkedIds.delete(cb.value)
    this.updateState()
  }

  updateState() {
    const count = this.checkedIds.size
    const atMax = count >= this.maxSelections

    if (atMax !== this._lastAtMax) {
      this.checkboxTargets.forEach(cb => {
        cb.disabled = atMax && !cb.checked
      })
      this._lastAtMax = atMax
    }

    if (this.hasViewBtnTarget) this.viewBtnTarget.disabled = count === 0
    if (this.hasCountTarget)   this.countTarget.textContent = count > 0 ? `${count} selected` : ""
    if (this.hasClearBtnTarget) this.clearBtnTarget.style.display = count > 0 ? "" : "none"
  }

  clearAll() {
    this.checkedIds.clear()
    this.checkboxTargets.forEach(cb => { cb.checked = false })
    this.updateState()
  }

  openDashboard(event) {
    event.preventDefault()
    const ids = Array.from(this.checkedIds)
    if (ids.length === 0) return

    const params = new URLSearchParams()
    ids.forEach(id => params.append("geography_ids[]", id))
    window.open(`/dashboard?${params.toString()}&period=90d`, "_blank")
  }

  // ---------- Group helpers ----------

  _checkboxesFor(group) {
    return this[`${group}CheckboxTargets`] || []
  }

  _checkedValues(group) {
    return this._checkboxesFor(group).filter(cb => cb.checked).map(cb => cb.value)
  }

  _updateGroupLabel(group) {
    const labelTarget = this[`has${group[0].toUpperCase()}${group.slice(1)}LabelTarget`]
      ? this[`${group}LabelTarget`]
      : null
    if (!labelTarget) return

    const values = this._checkedValues(group)
    const isInclude = group.endsWith("Include")

    if (values.length === 0) {
      labelTarget.textContent = isInclude ? "All" : "None"
      labelTarget.classList.remove("checkbox-filter-value--active")
      return
    }

    labelTarget.classList.add("checkbox-filter-value--active")
    if (values.length <= 2) {
      labelTarget.textContent = values.join(", ")
    } else {
      labelTarget.textContent = `${values.length} selected`
    }
  }
}
